import { environment } from 'src/environments/environment';
import { Injectable, InjectionToken, Inject, NgZone } from '@angular/core';
import { Gtag, EventParams, CustomParams, Product, Promotion, Action, Content } from './gtag-definitions';
import { Track } from '../shared/models/tracks-sfx/track.model';
import { CartItem } from '../shared/models/cart/cart-item.model';
import { GtagBaseService } from './gtag-base.service';
import { SoundEffect } from '../shared/models/sound-effect.model';
import { UtilsService } from '../shared/services/utils.service';

export const GTAG = new InjectionToken<Gtag>('snack.gtag.instance');

@Injectable()
export class GtagService extends GtagBaseService {

  constructor(
    @Inject(GTAG) gtag: Gtag,
    zone: NgZone,
    utils: UtilsService
  ) {
    super(gtag, zone, utils);
  }

  public pageView(title?: string, path?: string, location?: string) {
    return this.sendPageView(title, path, location);
  }

  public click(label: string) {
    this.sendClick({ event_label: label });
  }

  /**
   * Should be fired when changing a view layout (between grid and list).
   */
  public viewClick(isGrid: boolean) {
    this.sendViewClick({ event_label: isGrid ? 'tiles' : 'list' });
  }

  public timePlayed(timeInMinutes: number) {
    this.sendTimePlayed({ event_label: timeInMinutes?.toString() ?? '0' })
  }

  /**
   * When the user starts playing a new track.
   */
  public play(trackId: number) {
    this.sendPlay({ event_label: trackId?.toString() });
  }

  /**
   * When the user stops playing a track.
   */
  public stop(trackId: number) {
    this.sendStop({ event_label: trackId?.toString() });
  }

  public missingField(fieldName: string) {
    this.sendMissingField({ event_label: fieldName });
  }

  /**
   * When an incorrect password is entered on login.
   */
  public passwordError() {
    this.sendPassword({ event_label: 'incorrect' });
  }

  public open(label: string) {
    this.sendOpen({ event_label: label });
  }

  public close(label: string) {
    this.sendClose({ event_label: label });
  }

  public apply(label: string) {
    this.sendApply({ event_label: label });
  }

  public remove(label: string) {
    this.sendRemove({ event_label: label });
  }

  public login(label: string) {
    this.sendLogin(label);
  }

  public signUp() {
    this.sendSignUp('email');
  }

  // When user presses "switch to subscription plan" (experiment)
  public switchToSubscription(label: string) {
    this.sendSwitchToSubscription({ event_label: label });
  }

  /**
   * Sends a pageView event using document.title and window.location.
   * This should only be used when you are 100% certain the SEO service response has
   * already been impacted to the site HTML.
   */
  public pageViewWithWindowParams() {
    if (this.utils.isBrowser) {
      this.pageView(document.title, window.location.href.replace(window.location.origin, ""), window.location.href);
    }
  }

  public viewTrack(track: Track) {
    if (!track) {
      return;
    }
    const product = this.getTrackProduct(track);
    if (track.prices?.length) {
      product.price = track.prices[0].regularPrice.toFixed(2);
    }
    this.sendViewItem([product]);
  }

  public viewTracksList(tracks?: Track[]) {
    if (!tracks?.length) {
      return;
    }
    const products: Product[] = tracks.map(t => {
      const product = this.getTrackProduct(t);
      product.list_name = 'Overview';
      product.variant = '';
      product.list_position = 1;
      return product;
    });
    this.sendViewItemList(products);
  }

  public viewSfxsList(tracks?: SoundEffect[]) {
    if (!tracks?.length) {
      return;
    }
    const products: Product[] = tracks.map(t => {
      const product = this.getSfxProduct(t);
      product.list_name = 'Overview';
      product.variant = '';
      product.list_position = 1;
      return product;
    });
    this.sendViewItemList(products);
  }

  public addItemToCart(item: CartItem) {
    this.sendAddToCart({
      items: [this.getCartItemProduct(item)],
      event_label: this.getCheckoutEventLabel([item])
    });
  }

  public removeItemFromCart(item: CartItem) {
    this.sendRemoveFromCart({
      items: [this.getCartItemProduct(item)],
      event_label: this.getCheckoutEventLabel([item])
    });
  }

  public beginCheckout(items: CartItem[]) {
    const products = items.map(i => this.getCartItemProduct(i));
    this.sendBeginCheckout({
      items: products,
      event_label: this.getCheckoutEventLabel(items),
      coupon: ''
    });
  }

  public progressCheckout(items: CartItem[], discount: string) {
    const products = items.map(i => this.getCartItemProduct(i));
    this.sendCheckoutProgress({
      items: products,
      event_label: this.getCheckoutEventLabel(items),
      coupon: discount ?? ''
    });
  }

  public purchaseFree(data: Action) {
    if (data) {
      this.sendPurchase(data);
      // Events are sent async so we need to duplicate the data to prevent race conditions.
      const newData = JSON.parse(JSON.stringify(data));
      newData.send_to = environment.adWordsId + '/' + environment.adWordsFreeConversion;
      this.sendConversion(newData);
    }
  }

  public purchaseSongs(data: Action) {
    if (data) {
      this.sendPurchase(data);
      // Events are sent async so we need to duplicate the data to prevent race conditions.
      const newData = JSON.parse(JSON.stringify(data));
      newData.send_to = environment.adWordsId + '/' + environment.adWordsSongsConversion;
      this.sendConversion(newData);
    }
  }

  public purchaseSubscriptions(data: Action) {
    if (data) {
      this.sendPurchase(data);
      // Events are sent async so we need to duplicate the data to prevent race conditions.
      const newData = JSON.parse(JSON.stringify(data));
      newData.send_to = environment.adWordsId + '/' + environment.adWordsSubscriptionsConversion;
      this.sendConversion(newData);
    }
  }

  private getCartItemProduct(item: CartItem): Product {
    return {
      id: item.id.toString(),
      name: item.name,
      price: item.price,
      variant: item.variant,
      category: item.category,
      quantity: 1,
      list_name: 'Default'
    };
  }

  private getCheckoutEventLabel(items: CartItem[]): string {
    let label = '';

    if (items.find(i => i.isSubscription && i.isMonthly)) {
      if (label) {
        label += '|';
      }
      label += 'subscription_monthly';
    }
    if (items.find(i => i.isSubscription && !i.isMonthly)) {
      if (label) {
        label += '|';
      }
      label += 'subscription_yearly';
    }

    if (this.anyCartTrackHasPrice(items, 'broadcast')) {
      if (label) {
        label += '|';
      }
      label += 'single_broadcast';
    }
    if (this.anyCartTrackHasPrice(items, 'business')) {
      if (label) {
        label += '|';
      }
      label += 'single_business';
    }
    if (this.anyCartTrackHasPrice(items, 'pro')) {
      if (label) {
        label += '|';
      }
      label += 'single_pro';
    }
    if (this.anyCartTrackHasPrice(items, 'mention')) {
      if (label) {
        label += '|';
      }
      label += 'free_download';
    }

    return label;
  }

  private anyCartTrackHasPrice(items: CartItem[], price: string): boolean {
    for (let item of items) {
      var itemName = item.variant?.toLowerCase() ?? '';
      if (!item.isSubscription && itemName.indexOf(price) > -1) {
        return true;
      }
    }
    return false;
  }

  private getTrackProduct(track: Track): Product {
    return {
      id: track.id.toString(),
      name: track.title,
      category: Track.GetFirstCategoryName(track)
    };
  }

  private getSfxProduct(track: SoundEffect): Product {
    return {
      id: track.id.toString(),
      name: track.title,
      category: ''
    };
  }

}
